/**
 * Status Utility Functions
 *
 * Centralized logic for status calculations and mappings
 * following the modern design system conventions.
 */

/**
 * System-wide status types
 * @typedef {'all_operational' | 'partial_outage' | 'major_outage' | 'maintenance'} SystemStatus
 */

/**
 * Incident status types
 * @typedef {'investigating' | 'identified' | 'monitoring' | 'resolved' | 'OPEN' | 'CLOSED'} IncidentStatus
 */

/**
 * Priority levels
 * @typedef {'EP1' | 'EP2' | 'EP3' | 'EP4'} Priority
 */

/**
 * Derive system-wide status from array of problems
 * @param {Array} problems - Array of problem objects
 * @returns {{ status: SystemStatus, message: string, incidentCount: number }}
 */
export const deriveSystemStatus = (problems = []) => {
  if (!problems || problems.length === 0) {
    return {
      status: 'all_operational',
      message: 'All systems are operating normally.',
      incidentCount: 0
    };
  }

  // Check for EP1 (critical) incidents
  const hasEP1 = problems.some(p =>
    p.priority?.toUpperCase() === 'EP1'
  );

  // Check for EP2 (major) incidents
  const hasEP2 = problems.some(p =>
    p.priority?.toUpperCase() === 'EP2'
  );

  // Count active incidents
  const activeCount = problems.length;

  if (hasEP1) {
    return {
      status: 'major_outage',
      message: `${activeCount} active incident${activeCount > 1 ? 's' : ''} affecting systems.`,
      incidentCount: activeCount
    };
  }

  if (hasEP2) {
    return {
      status: 'partial_outage',
      message: `${activeCount} active incident${activeCount > 1 ? 's' : ''} with degraded performance.`,
      incidentCount: activeCount
    };
  }

  // EP3 or EP4 incidents
  return {
    status: 'partial_outage',
    message: `${activeCount} active incident${activeCount > 1 ? 's' : ''} being monitored.`,
    incidentCount: activeCount
  };
};

/**
 * Get display text for system status
 * @param {SystemStatus} status
 * @returns {string}
 */
export const getStatusDisplayText = (status) => {
  const textMap = {
    'all_operational': 'All Systems Operational',
    'partial_outage': 'Partial System Outage',
    'major_outage': 'Major System Outage',
    'maintenance': 'Scheduled Maintenance'
  };
  return textMap[status] || 'All Systems Operational';
};

/**
 * Get CSS class suffix for status
 * @param {SystemStatus} status
 * @returns {string}
 */
export const getStatusClass = (status) => {
  const classMap = {
    'all_operational': 'operational',
    'partial_outage': 'degraded',
    'major_outage': 'major_outage',
    'maintenance': 'maintenance'
  };
  return classMap[status] || 'operational';
};

/**
 * Get color CSS variable for priority
 * @param {Priority} priority
 * @returns {string}
 */
export const getPriorityColor = (priority) => {
  const colorMap = {
    'EP1': 'var(--color-priority-ep1)',
    'EP2': 'var(--color-priority-ep2)',
    'EP3': 'var(--color-priority-ep3)',
    'EP4': 'var(--color-priority-ep4)'
  };
  return colorMap[priority?.toUpperCase()] || 'var(--color-text-secondary)';
};

/**
 * Get Tailwind class for priority badge
 * @param {Priority} priority
 * @returns {string}
 */
export const getPriorityBadgeClass = (priority) => {
  const classMap = {
    'EP1': 'tw-bg-priority-ep1',
    'EP2': 'tw-bg-priority-ep2',
    'EP3': 'tw-bg-priority-ep3',
    'EP4': 'tw-bg-priority-ep4'
  };
  return classMap[priority?.toUpperCase()] || 'tw-bg-gray-500';
};

/**
 * True when a PRB's status means the incident is resolved.
 *
 * US2072496: this is the single definition of "resolved" for the Modern UI.
 * The canonical backend value is `types.OutageStatusResolve` — the exact string
 * "Resolve" (see backend/types/types.go; PRB statuses are "Log / Classify",
 * "Diagnose", "Resolve"). Only case and surrounding whitespace are tolerated.
 *
 * "RESOLVED" and "CLOSED" are deliberately NOT accepted: they are not values the
 * backend produces. IncidentListRow previously compared against those two
 * literals, so `"Resolve".toUpperCase()` matched neither and every resolved row
 * silently fell back to its start date. Keeping one predicate here is what stops
 * that comparison from drifting out of sync between the row, the card, and the
 * incident detail header again.
 *
 * @param {string} status
 * @returns {boolean}
 */
export const isResolvedStatus = (status) =>
  typeof status === 'string' && status.trim().toUpperCase() === 'RESOLVE';

/**
 * Get display text for incident status
 * @param {IncidentStatus} status
 * @returns {string}
 */
export const getIncidentStatusText = (status) => {
  const statusMap = {
    'OPEN': 'Investigating',
    'investigating': 'Investigating',
    'identified': 'Identified',
    'monitoring': 'Monitoring',
    'resolved': 'Resolved',
    'RESOLVE': 'Resolved',
    'CLOSED': 'Resolved'
  };
  return statusMap[status?.toUpperCase?.()] || statusMap[status] || status;
};

/**
 * Format timestamp for display
 * @param {string|Date} timestamp
 * @param {Object} options
 * @param {boolean} options.relative - Show relative time (e.g., "2h ago")
 * @param {boolean} options.includeTime - Include time in absolute format
 * @returns {string}
 */
export const formatTimestamp = (timestamp, options = {}) => {
  const { relative = true, includeTime = true } = options;

  if (!timestamp) return '';

  // Normalize Unix timestamps: detect seconds vs milliseconds.
  // Timestamps below 1e12 (~2001 in ms, ~33658 in s) are treated as seconds.
  const normalized = typeof timestamp === 'number' && timestamp > 0 && timestamp < 1e12
    ? timestamp * 1000
    : timestamp;

  const date = new Date(normalized);

  if (isNaN(date.getTime())) return '';

  if (relative) {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
  }

  // Absolute format
  const formatOptions = {
    month: 'short',
    day: 'numeric'
  };

  if (includeTime) {
    formatOptions.hour = 'numeric';
    formatOptions.minute = '2-digit';
  }

  return date.toLocaleDateString('en-US', formatOptions);
};

/**
 * Get priority display label with severity indicator
 * @param {Priority} priority
 * @returns {{ label: string, severity: string }}
 */
export const getPriorityInfo = (priority) => {
  const infoMap = {
    'EP1': { label: 'EP1', severity: 'Critical' },
    'EP2': { label: 'EP2', severity: 'Major' },
    'EP3': { label: 'EP3', severity: 'Minor' },
    'EP4': { label: 'EP4', severity: 'Low' }
  };
  return infoMap[priority?.toUpperCase()] || { label: priority, severity: 'Unknown' };
};

const statusUtils = {
  deriveSystemStatus,
  getStatusDisplayText,
  getStatusClass,
  getPriorityColor,
  getPriorityBadgeClass,
  isResolvedStatus,
  getIncidentStatusText,
  formatTimestamp,
  getPriorityInfo
};

export default statusUtils;
